import { useId } from "react";
import { useResourceTags } from "@/lib/useResourceTags";
import { Button } from "./Button";
import { TextInput } from "./TextInput";

export interface Tag {
  key: string;
  value: string;
}

interface TagsEditorProps {
  tags: Tag[];
  onChange: (tags: Tag[]) => void;
}

export function TagsEditor({ tags, onChange }: TagsEditorProps) {
  const listId = useId();
  const { tagKeys, tagValuesFor } = useResourceTags();

  function addTag() {
    onChange([...tags, { key: "", value: "" }]);
  }

  function removeTag(index: number) {
    onChange(tags.filter((_, i) => i !== index));
  }

  function updateTag(index: number, field: "key" | "value", value: string) {
    onChange(
      tags.map((tag, i) => (i === index ? { ...tag, [field]: value } : tag)),
    );
  }

  return (
    <div>
      <span className="mb-1.5 block text-sm text-foreground">
        Tags <span className="text-foreground/70">(optional)</span>
      </span>
      <div className="space-y-2">
        <datalist id={`${listId}-keys`}>
          {tagKeys.map((key) => (
            <option key={key} value={key} />
          ))}
        </datalist>
        {tags.map((tag, index) => (
          <div key={index} className="flex items-center gap-2">
            <TextInput
              value={tag.key}
              onChange={(e) => updateTag(index, "key", e.target.value)}
              list={`${listId}-keys`}
              placeholder="Key"
              className="min-w-0 flex-1"
            />
            <TextInput
              value={tag.value}
              onChange={(e) => updateTag(index, "value", e.target.value)}
              list={`${listId}-values-${index}`}
              placeholder="Value"
              className="min-w-0 flex-1"
            />
            <datalist id={`${listId}-values-${index}`}>
              {tagValuesFor(tag.key).map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
            <Button
              onClick={() => removeTag(index)}
              variant="secondary"
              className="shrink-0 px-3"
            >
              Remove
            </Button>
          </div>
        ))}
        <Button onClick={addTag} variant="secondary">
          Add tag
        </Button>
      </div>
    </div>
  );
}
